import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { inputService, caseService, userService } from "@/services/api";
import { mapDbCaseToFrontend } from "@/services/mapping";
import Button from "@/components/base/Button";

interface DocenteItem {
  id: string;
  name: string;
}

export default function OrientacionInsumosPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [insumos, setInsumos] = useState<any[]>([]);
  const [docentes, setDocentes] = useState<DocenteItem[]>([]);

  // Reminders states
  const [remindedIds, setRemindedIds] = useState<Record<string, boolean>>({});

  // Reassignment states
  const [reasigningId, setReasigniningId] = useState<string | null>(null);
  const [newDocenteId, setNewDocenteId] = useState("");

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");
      const [inputsData, usersData] = await Promise.all([
        inputService.listSolicitados(),
        userService.list()
      ]);

      if (Array.isArray(inputsData)) {
        setInsumos(inputsData.map((d: any) => ({
          id: d.id,
          caseId: d.caso_id,
          casoFolio: d.caso_folio || "CASO",
          casoTitulo: d.caso_titulo || "Caso",
          studentName: d.estudiante_nombre || "Estudiante",
          curso: d.estudiante_curso || "Sin curso",
          docenteId: d.docente_id,
          docenteName: d.docente_nombre || "Docente",
          tipoInsumo: d.tipo_insumo || "Insumo docente",
          estado: d.estado || "pendiente",
          fechaLimite: d.plazo_entrega ? new Date(d.plazo_entrega).toISOString().slice(0, 10) : "",
          respuesta: d.respuesta || ""
        })));
      }

      if (Array.isArray(usersData)) {
        setDocentes(usersData.filter((u: any) => u.rol === "profesor" || u.rol === "profesor_jefe" || u.rol === "docente").map((u: any) => ({
          id: u.id,
          name: `${u.nombre} (${u.cargo || "Docente"})`
        })));
      }
    } catch (err: any) {
      console.error("Error loading insumos data:", err);
      setError("Error al cargar solicitudes de insumos desde Azure: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filtered = useMemo(() => {
    if (!statusFilter) return insumos;
    return insumos.filter((ti) => ti.estado === statusFilter);
  }, [statusFilter, insumos]);

  const pendientes = insumos.filter((t) => t.estado === "pendiente" || t.estado === "solicitado");
  const vencidos = insumos.filter((t) => t.estado === "vencido");
  const entregados = insumos.filter((t) => t.estado === "entregado" || t.estado === "enviado" || t.estado === "aprobado");

  const handleRecordar = (id: string) => {
    setRemindedIds(prev => ({ ...prev, [id]: true }));
    setTimeout(() => {
      setRemindedIds(prev => ({ ...prev, [id]: false }));
    }, 2500);
  };

  const handleOpenReasignar = (id: string, currentDocenteId: string) => {
    setReasigniningId(id);
    setNewDocenteId(currentDocenteId);
  };

  const handleReasignarSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reasigningId) return;

    try {
      await inputService.update(reasigningId, {
        docente_id: newDocenteId
      });
      setReasigniningId(null);
      await loadData();
    } catch (err: any) {
      alert("Error al reasignar docente en base de datos: " + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <i className="ri-loader-4-line text-2xl text-foreground-300 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5 animate-fade-in-up">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-800 text-xs rounded-xl font-medium">
          {error}
        </div>
      )}

      <button
        onClick={() => navigate("/orientacion")}
        className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-line" />
        Volver a Orientación
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Insumos y Solicitudes Docentes</h1>
          <p className="text-sm text-foreground-500 mt-0.5">Control de bitácoras, respuestas y reportes solicitados a profesores de asignatura.</p>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-background-200 rounded-xl p-4 flex flex-col justify-between shadow-3xs">
          <span className="text-2xs font-bold text-foreground-450 uppercase tracking-wider">Pendientes</span>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="text-2xl font-bold text-foreground-900">{pendientes.length}</span>
            <span className="text-xs text-foreground-500">solicitudes</span>
          </div>
        </div>
        <div className="bg-white border border-background-200 rounded-xl p-4 flex flex-col justify-between shadow-3xs">
          <span className="text-2xs font-bold text-red-600 uppercase tracking-wider">Fuera de Plazo</span>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="text-2xl font-bold text-red-600">{vencidos.length}</span>
            <span className="text-xs text-red-500 font-medium">vencidas</span>
          </div>
        </div>
        <div className="bg-white border border-background-200 rounded-xl p-4 flex flex-col justify-between shadow-3xs">
          <span className="text-2xs font-bold text-accent-600 uppercase tracking-wider">Recepcionados</span>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="text-2xl font-bold text-accent-600">{entregados.length}</span>
            <span className="text-xs text-accent-500">respuestas</span>
          </div>
        </div>
      </div>

      {/* Filter and Content */}
      <div className="bg-white border border-background-200 rounded-2xl overflow-hidden shadow-3xs">
        <div className="p-4 border-b border-background-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-background-50/20">
          <h2 className="text-sm font-semibold text-foreground-900">Listado General</h2>
          <div className="flex items-center gap-2">
            <span className="text-xs text-foreground-500">Filtrar estado:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-xs border border-background-200 bg-white px-2.5 py-1.5 rounded-lg outline-none text-foreground-700 cursor-pointer"
            >
              <option value="">Todos</option>
              <option value="pendiente">Pendiente</option>
              <option value="enviado">Entregado</option>
              <option value="vencido">Vencido</option>
            </select>
          </div>
        </div>

        <div className="divide-y divide-background-100">
          {filtered.map((item) => (
            <div key={item.id} className="p-4 hover:bg-background-50/40 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2.5">
                  <span className={`text-3xs font-bold px-2 py-0.5 rounded-full ${
                    item.estado === "enviado" || item.estado === "entregado" || item.estado === "aprobado"
                      ? "bg-accent-100 text-accent-800"
                      : item.estado === "vencido"
                      ? "bg-red-100 text-red-800"
                      : "bg-amber-100 text-amber-800"
                  }`}>
                    {item.estado === "enviado" || item.estado === "entregado" ? "ENTREGADO" : item.estado.toUpperCase()}
                  </span>
                  <span className="text-xs font-mono text-foreground-500">{item.casoFolio}</span>
                  <span className="text-xs text-foreground-450">Estudiante: {item.studentName} ({item.curso})</span>
                </div>
                <p className="text-sm font-semibold text-foreground-900">{item.tipoInsumo}</p>
                <p className="text-xs text-foreground-500">Docente asignado: <strong className="text-foreground-700">{item.docenteName}</strong></p>
                
                {item.respuesta && (
                  <div className="mt-2 p-2.5 bg-background-50 rounded-lg border border-background-100 text-xs text-foreground-600 font-sans italic max-w-xl">
                    "{item.respuesta}"
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 self-end sm:self-center">
                {item.estado === "pendiente" && (
                  <>
                    <button
                      onClick={() => handleRecordar(item.id)}
                      className={`text-xs px-3 py-1.5 rounded-lg border transition-all duration-200 flex items-center gap-1 cursor-pointer font-medium ${
                        remindedIds[item.id]
                          ? "bg-accent-50 border-accent-200 text-accent-600 scale-95"
                          : "bg-white border-background-200 text-foreground-600 hover:bg-background-50"
                      }`}
                      disabled={remindedIds[item.id]}
                    >
                      <i className={remindedIds[item.id] ? "ri-check-line text-xs" : "ri-mail-send-line text-xs"} />
                      {remindedIds[item.id] ? "Notificado" : "Enviar Recordatorio"}
                    </button>
                    <button
                      onClick={() => handleOpenReasignar(item.id, item.docenteId)}
                      className="text-xs px-3 py-1.5 rounded-lg border border-background-200 bg-white text-foreground-600 hover:bg-background-50 transition-colors cursor-pointer font-medium"
                    >
                      Reasignar
                    </button>
                  </>
                )}
                <span className="text-3xs text-foreground-450 font-medium">Plazo: {item.fechaLimite}</span>
              </div>
            </div>
          ))}

          {filtered.length === 0 && (
            <p className="text-sm text-foreground-450 text-center py-10">No hay registros de insumos que coincidan con el filtro.</p>
          )}
        </div>
      </div>

      {/* ── MODAL: REASIGNAR DOCENTE ── */}
      {reasigningId && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setReasigniningId(null)}>
          <form onSubmit={handleReasignarSubmit} className="bg-white rounded-2xl w-full max-w-sm p-6 space-y-4 shadow-xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center pb-2 border-b border-background-150">
              <h2 className="font-bold text-foreground-950 text-base">Reasignar Docente</h2>
              <button type="button" onClick={() => setReasigniningId(null)} className="cursor-pointer text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nuevo Docente Responsable</label>
              <select
                value={newDocenteId}
                onChange={(e) => setNewDocenteId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
                required
              >
                <option value="">Seleccionar docente...</option>
                {docentes.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setReasigniningId(null)} className="flex-1 cursor-pointer">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-primary-500 hover:bg-primary-600 text-white cursor-pointer">
                Guardar Reasignación
              </Button>
            </div>
          </form>
        </div>,
        document.body
      )}
    </div>
  );
}